import React, { useEffect, useState } from 'react';
import { Lock, MapPin, Radio } from 'lucide-react';
import { Card, Pill, SectionHeader } from './ui';
import { studentApi } from '../../pages/journey/journeyApi';

const SOURCE = { greenhouse: 'Company careers page', lever: 'Company careers page', smartrecruiters: 'Company careers page', jsearch: 'Job boards', elevate: 'Verified by Elevate' };

const JobCard = ({ j }) => (
  <Card className="flex flex-col" data-testid={`job-${j.id}`}>
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <Pill className="bg-emerald-50 text-emerald-700" data-testid="job-live-badge"><Radio className="w-3 h-3" /> Live · {j.publisher || SOURCE[j.source]}</Pill>
      {j.is_new && <Pill className="bg-amber-100 text-amber-800 font-semibold" data-testid={`job-new-badge-${j.id}`}>New this week</Pill>}
      {j.entry_level && <Pill className="bg-teal-50 text-teal-700">Entry-level</Pill>}
    </div>
    <p className="font-outfit font-semibold text-lg text-[#0b1f4d] leading-snug">{j.title}</p>
    <p className="text-sm text-slate-600">{j.company}</p>
    {j.location && <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><MapPin className="w-3.5 h-3.5" /> {j.location}</p>}
    {j.posted_at && <p className="text-xs text-slate-400 mt-1">Posted/updated {j.posted_at}</p>}
    <div className="mt-auto pt-5 flex items-center gap-3">
      <button disabled className="inline-flex items-center gap-2 bg-slate-200 text-slate-500 text-sm font-semibold px-4 py-2 rounded-full cursor-not-allowed" data-testid={`job-apply-locked-${j.id}`}><Lock className="w-3.5 h-3.5" /> Apply</button>
    </div>
  </Card>
);

const Group = ({ g }) => (
  <section className="mb-10" data-testid={`job-group-${g.title}`}>
    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
      <h2 className="font-outfit text-lg font-semibold text-[#0b1f4d]">{g.title}</h2>
      <span className="flex items-center gap-2 text-xs text-slate-500">
        {g.new_this_week > 0 && <Pill className="bg-amber-100 text-amber-800 font-semibold" data-testid={`job-group-new-${g.title}`}>{g.new_this_week} new this week</Pill>}
        {g.total_found} live opening{g.total_found === 1 ? '' : 's'} found
      </span>
    </div>
    {g.jobs.length ? <div className="grid md:grid-cols-3 gap-4">{g.jobs.map((j) => <JobCard key={j.id} j={j} />)}</div>
      : <Card className="text-sm text-slate-500" data-testid="job-group-empty">No live openings matched right now. Listings refresh every morning.</Card>}
    {g.more_count > 0 && (
      <button disabled data-testid={`job-view-more-locked-${g.title}`}
        className="mt-4 w-full flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 bg-white/60 py-4 text-sm font-semibold text-slate-500 cursor-not-allowed">
        <Lock className="w-4 h-4" /> View {g.more_count} more {g.title} opportunit{g.more_count === 1 ? 'y' : 'ies'}
      </button>)}
  </section>
);

const Expectations = ({ roles }) => roles.map((r) => (
  <Card key={r.id} className="mt-2 mb-5" data-testid={`role-expectations-${r.id}`}>
    <p className="text-[11px] uppercase tracking-[0.2em] text-teal-700 font-semibold mb-2">What employers expect</p>
    <p className="font-outfit font-semibold text-xl text-[#0b1f4d] mb-4">{r.title}</p>
    <ul className="grid md:grid-cols-2 gap-2 text-sm text-slate-700">{r.expectations.map((x) => <li key={x}>✓ {x}</li>)}</ul>
  </Card>
));

export const Opportunities = ({ data: d, studentId, refresh }) => {
  const [res, setRes] = useState(null);
  const targetsKey = (d.target_roles || []).map((t) => t.title || t).join('|');
  useEffect(() => {
    studentApi(`/me/opportunities?student_id=${studentId}`).then((r) => {
      setRes(r);
      studentApi('/me/opportunities/seen', { method: 'POST', body: { student_id: studentId } }).then(refresh).catch(() => {});
    }).catch(() => setRes({ groups: [] }));
  }, [targetsKey, studentId]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <SectionHeader eyebrow={res?.browse_all ? 'Real openings across career paths' : 'Real openings for your target roles'} title="Career Opportunities">Prepare for roles like these: build the practical skills and career readiness employers expect.</SectionHeader>
      {res?.browse_all && res.groups.length > 0 && <Card className="text-sm text-slate-600 mb-6 bg-teal-50 border-teal-100" data-testid="jobs-browse-all-note"><strong className="text-[#0b1f4d]">Showing openings across all career paths.</strong> Choose 2–3 target roles on your Overview to see openings picked for you.</Card>}
      {!res ? <div className="grid md:grid-cols-3 gap-4" data-testid="jobs-loading">{[0, 1, 2].map((i) => <div key={i} className="h-48 bg-white rounded-2xl animate-pulse" />)}</div>
        : res.groups.length ? res.groups.map((g) => <Group key={g.title} g={g} />)
        : <Card className="text-sm text-slate-600 mb-6" data-testid="jobs-no-target-roles">No target roles are set yet. Choose 2–3 target roles on your Overview and ask your consultant to confirm them to see live openings here.</Card>}
      <Expectations roles={d.roles} />
    </>
  );
};
